import { BaseService, createApiResponse } from './BaseService';

export class UserService extends BaseService {

  async getUsers(filters = {}) {
    await this.simulateDelay();
    
    const cacheKey = 'users';
    const cached = this.getCached(cacheKey);
    
    if (cached) {
      return createApiResponse(cached, 'Users retrieved from cache');
    }
    
    const users = [
      {
        id: '1',
        name: 'John Doe',
        email: 'john.doe@company.com',
        role: 'admin',
        department: 'IT',
        status: 'active',
        createdAt: '2024-01-01T00:00:00Z',
        lastLogin: '2024-01-20T10:30:00Z',
        permissions: ['read', 'write', 'admin'],
        profile: {
          avatar: null,
          phone: '+1-555-0123',
          timezone: 'America/New_York',
          language: 'en'
        }
      },
      {
        id: '2',
        name: 'Jane Smith',
        email: 'jane.smith@company.com',
        role: 'agent',
        department: 'Support',
        status: 'active',
        createdAt: '2024-01-02T00:00:00Z',
        lastLogin: '2024-01-20T09:15:00Z',
        permissions: ['read', 'write'],
        profile: {
          avatar: null,
          phone: '+1-555-0124',
          timezone: 'America/New_York',
          language: 'en'
        }
      },
      {
        id: '3',
        name: 'Mike Johnson',
        email: 'mike.johnson@company.com',
        role: 'customer',
        department: null,
        status: 'active',
        createdAt: '2024-01-03T00:00:00Z',
        lastLogin: '2024-01-19T16:45:00Z',
        permissions: ['read'],
        profile: {
          avatar: null,
          phone: '+1-555-0125',
          timezone: 'America/Chicago',
          language: 'en'
        }
      }
    ];
    
    this.setCache(cacheKey, users);
    return createApiResponse(users, 'Users retrieved successfully');
  }

  async getUser(id) {
    await this.simulateDelay();
    
    const usersResponse = await this.getUsers();
    const user = usersResponse.data.find(u => u.id === id);
    
    if (!user) {
      return createApiResponse(null, 'User not found', false);
    }
    
    return createApiResponse(user, 'User retrieved successfully');
  }

  async createUser(userData) {
    await this.simulateDelay();
    
    const newUser = {
      id: `user_${Date.now()}`,
      ...userData,
      createdAt: new Date().toISOString(),
      lastLogin: null,
      permissions: userData.permissions || ['read'],
      profile: {
        avatar: null,
        phone: userData.phone || '',
        timezone: userData.timezone || 'America/New_York',
        language: userData.language || 'en',
        ...userData.profile
      }
    };
    
    // Clear cache to force refresh
    this.clearCache();
    
    return createApiResponse(newUser, 'User created successfully');
  }

  async updateUser(id, userData) {
    await this.simulateDelay();
    
    const updatedUser = {
      ...userData,
      id,
      updatedAt: new Date().toISOString()
    };
    
    // Clear cache to force refresh
    this.clearCache();
    
    return createApiResponse(updatedUser, 'User updated successfully');
  }

  async deleteUser(id) {
    await this.simulateDelay();
    
    // Clear cache to force refresh
    this.clearCache();
    
    return createApiResponse({ id }, 'User deleted successfully');
  }

  async getAgents() {
    await this.simulateDelay();
    
    const cacheKey = 'agents';
    const cached = this.getCached(cacheKey);
    
    if (cached) {
      return createApiResponse(cached, 'Agents retrieved from cache');
    }
    
    const agents = [
      {
        id: 'agent_1',
        name: 'Sarah Wilson',
        email: 'sarah.wilson@company.com',
        role: 'senior_agent',
        department: 'Technical Support',
        status: 'active',
        skills: ['technical', 'networking', 'database'],
        workload: {
          currentTickets: 8,
          maxTickets: 15,
          utilization: 53.3
        },
        performance: {
          avgResolutionTime: 2.5,
          customerSatisfaction: 4.8,
          ticketsResolved: 156
        },
        availability: {
          status: 'online',
          schedule: '9am-5pm EST',
          timezone: 'America/New_York'
        },
        createdAt: '2024-01-01T00:00:00Z',
        lastActive: '2024-01-20T10:30:00Z'
      },
      {
        id: 'agent_2',
        name: 'David Brown',
        email: 'david.brown@company.com',
        role: 'agent',
        department: 'Customer Support',
        status: 'active',
        skills: ['customer_service', 'billing', 'general'],
        workload: {
          currentTickets: 12,
          maxTickets: 20,
          utilization: 60.0
        },
        performance: {
          avgResolutionTime: 1.8,
          customerSatisfaction: 4.6,
          ticketsResolved: 203
        },
        availability: {
          status: 'online',
          schedule: '8am-4pm EST',
          timezone: 'America/New_York'
        },
        createdAt: '2024-01-02T00:00:00Z',
        lastActive: '2024-01-20T10:25:00Z'
      }
    ];
    
    this.setCache(cacheKey, agents);
    return createApiResponse(agents, 'Agents retrieved successfully');
  }

  async getAgentPerformanceMetrics(agentId) {
    await this.simulateDelay();
    
    const metrics = {
      agentId,
      period: 'last_30_days',
      ticketsResolved: Math.floor(Math.random() * 100) + 50,
      avgResolutionTime: Math.random() * 5 + 1,
      customerSatisfaction: Math.random() * 2 + 3,
      firstCallResolution: Math.random() * 0.3 + 0.7,
      responseTime: Math.random() * 2 + 0.5,
      escalationRate: Math.random() * 0.2 + 0.05,
      workloadUtilization: Math.random() * 40 + 50
    };
    
    return createApiResponse(metrics, 'Agent performance metrics retrieved successfully');
  }

  async getAssignmentRules() {
    await this.simulateDelay();
    
    const rules = [
      {
        id: 'rule_1',
        name: 'Round Robin Assignment',
        type: 'round_robin',
        priority: 1,
        conditions: {
          department: 'support',
          skillMatch: true
        },
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      },
      {
        id: 'rule_2',
        name: 'Skill-Based Assignment',
        type: 'skill_based',
        priority: 2,
        conditions: {
          skillMatch: true,
          workloadLimit: 80
        },
        isActive: true,
        createdAt: '2024-01-02T00:00:00Z'
      }
    ];
    
    return createApiResponse(rules, 'Assignment rules retrieved successfully');
  }

  async getAvailabilitySettings() {
    await this.simulateDelay();
    
    const settings = {
      businessHours: {
        monday: { start: '09:00', end: '17:00', enabled: true },
        tuesday: { start: '09:00', end: '17:00', enabled: true },
        wednesday: { start: '09:00', end: '17:00', enabled: true },
        thursday: { start: '09:00', end: '17:00', enabled: true },
        friday: { start: '09:00', end: '17:00', enabled: true },
        saturday: { start: '10:00', end: '14:00', enabled: false },
        sunday: { start: '10:00', end: '14:00', enabled: false }
      },
      timezone: 'America/New_York',
      holidays: [
        { date: '2024-01-01', name: 'New Year\'s Day' },
        { date: '2024-07-04', name: 'Independence Day' },
        { date: '2024-12-25', name: 'Christmas Day' }
      ],
      maxConcurrentTickets: 20,
      breakSettings: {
        duration: 15,
        frequency: 120
      }
    };
    
    return createApiResponse(settings, 'Availability settings retrieved successfully');
  }
}
